import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";

function friendlyError(code) {
  const map = {
    "auth/invalid-email": "That email address doesn't look right.",
    "auth/user-not-found": "No account found with those details.",
    "auth/wrong-password": "Incorrect email or password.",
    "auth/invalid-credential": "Incorrect email or password.",
    "auth/email-already-in-use": "An account already exists for that email.",
    "auth/weak-password": "Password should be at least 6 characters.",
    "auth/too-many-requests": "Too many attempts. Please try again shortly.",
  };
  return map[code] || "Something went wrong. Please try again.";
}

export default function AuthScreen() {
  const { login, signup } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const isLogin = mode === "login";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (isLogin) await login(email, password);
      else await signup(email, password);
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-shell" data-testid="auth-screen">
      <div className="auth-card">
        <div className="auth-brand">
          <span className="brand-mark">BG</span>
          <div>
            <div className="brand-name">NORTHAG / CENTAG</div>
            <div className="brand-sub">BATTLEGROUP LIST BUILDER</div>
          </div>
        </div>

        <h1 className="auth-title" data-testid="auth-title">{isLogin ? "Sign in to continue" : "Create your account"}</h1>
        <p className="auth-desc">{isLogin ? "Access is restricted to authenticated commanders." : "Register to start building battlegroups."}</p>

        <form onSubmit={submit} className="auth-form">
          <label className="auth-label" htmlFor="auth-email">Email</label>
          <input
            id="auth-email"
            className="auth-input"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="auth-email-input"
          />
          <label className="auth-label" htmlFor="auth-password">Password</label>
          <div className="auth-pw-wrap">
            <input
              id="auth-password"
              className="auth-input"
              type={showPassword ? "text" : "password"}
              autoComplete={isLogin ? "current-password" : "new-password"}
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="auth-password-input"
            />
            <button
              type="button"
              className="auth-pw-toggle"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              title={showPassword ? "Hide password" : "Show password"}
              data-testid="auth-password-toggle"
            >
              {showPassword ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>

          {error ? <div className="auth-error" data-testid="auth-error">{error}</div> : null}

          <button type="submit" className="btn btn-primary auth-submit" disabled={busy} data-testid="auth-submit-btn">
            {busy ? "Please wait\u2026" : isLogin ? "Log In" : "Sign Up"}
          </button>
        </form>

        <div className="auth-switch">
          {isLogin ? "Need an account?" : "Already have an account?"}{" "}
          <button
            type="button"
            className="auth-link"
            onClick={() => { setMode(isLogin ? "signup" : "login"); setError(""); }}
            data-testid="auth-toggle-btn"
          >
            {isLogin ? "Sign Up" : "Log In"}
          </button>
        </div>
      </div>
    </div>
  );
}
